import React, { useState, useEffect } from 'react';
import Notification from '../common/Notification';
import AttendanceSkeleton from '../common/AttendanceSkeleton';
import '../../styles/Attendance.css';
import { 
  fetchStudentsByClass, 
  fetchAttendance, 
  fetchStaffs, 
  fetchTeacherAttendance, 
  updateAttendanceBulk,
  updateStaffAttendanceBulk 
} from '../../utils/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

function Attendance() {
  const { user } = useAuth(); // get user with role

  const currentDate = new Date();
  
  // State variables
  const [profile, setProfile] = useState('students'); // 'students' or 'teachers'
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [classes, ] = useState(['NUR', 'KG1', 'KG2', 'STD1', 'STD2', 'STD3', 'STD4', 'STD5', 'STD6', 'STD7', 'STD8', 'STD9', 'STD10', 'STD11', 'STD12']);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [updateProgress, setUpdateProgress] = useState({ current: 0, total: 0 });
  
  // Get days in month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };
  
  const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
  const dayNumbers = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  
  // Month names for dropdown
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];
  
  // Years for dropdown (current year and previous 2 years)
  const years = [
    currentDate.getFullYear(),
    currentDate.getFullYear() - 1,
    currentDate.getFullYear() - 2
  ];
  
  // Check if a day is today
  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      selectedMonth === today.getMonth() + 1 &&
      selectedYear === today.getFullYear()
    );
  };

  // Check if attendance can be edited for a specific day
  const canEditAttendance = (day) => {
    return isToday(day);
  };
  
  // Handle class change
  const handleClassChange = (e) => {
    setSelectedClass(e.target.value);
  };
  
  // Handle month change
  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
  };
  
  // Handle year change
  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };
  
  // Handle profile change
  const handleProfileChange = (e) => {
    setProfile(e.target.value);
  };
  
  // Load data based on selected profile
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      
      try {
        if (profile === 'students') {
          // Only load student data if a class is selected
          if (!selectedClass) {
            setStudents([]);
            setAttendanceData({});
            setIsLoading(false);
            return;
          }
          
          // Load students for the selected class
          const studentsData = await fetchStudentsByClass(selectedClass);
          const activeStudents = studentsData.filter(student => student.Active);
          setStudents(activeStudents);
          
          // Initialize attendance data
          const attendanceForMonth = await fetchAttendance(
            selectedClass,
            selectedMonth,
            selectedYear
          );
          
          // Create default attendance object
          const attendanceMap = {};
          activeStudents.forEach(student => {
            attendanceMap[student.Rno] = {};
            for (let i = 1; i <= daysInMonth; i++) {
              // Check if we have attendance data for this student and day
              if (attendanceForMonth[student.Rno]) {
                attendanceMap[student.Rno][i] = attendanceForMonth[student.Rno][i] || false;
              } else {
                attendanceMap[student.Rno][i] = false;
              }
            }
          });
          
          setAttendanceData(attendanceMap);
        } else if (profile === 'teachers') {
          // Load all teachers
          const teachersData = await fetchStaffs();
          setStudents(teachersData); // Reuse students state for teachers
          
          // Initialize attendance data for teachers
          const attendanceForMonth = await fetchTeacherAttendance(
            selectedMonth,
            selectedYear
          );
          
          // Create default attendance object
          const attendanceMap = {};
          teachersData.forEach(teacher => {
            attendanceMap[teacher.staff_no] = {};
            for (let i = 1; i <= daysInMonth; i++) {
              // Check if we have attendance data for this teacher and day
              if (attendanceForMonth[teacher.staff_no]) {
                attendanceMap[teacher.staff_no][i] = attendanceForMonth[teacher.staff_no][i] || false;
              } else {
                attendanceMap[teacher.staff_no][i] = false;
              }
            }
          });
          
          setAttendanceData(attendanceMap);
        }
      } catch (error) {
        console.error(`Error loading ${profile}:`, error);
        showNotification(`Failed to load ${profile}. Please try again.`, 'error');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [profile, selectedClass, selectedMonth, selectedYear, daysInMonth]);
  
  // Handle attendance change
  const handleAttendanceChange = (studentId, day, present) => {
    // Only allow changes for today's date
    if (isToday(day)) {
      setAttendanceData(prevData => ({
        ...prevData,
        [studentId]: {
          ...prevData[studentId],
          [day]: present
        }
      }));
    }
  };
  
  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
  };
  
  // Close notification
  const closeNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };
  
  // Get attendance summary for today (students or teachers)
  const getAttendanceSummary = () => {
    const today = new Date().getDate();
    let presentCount = 0;
    let total = 0;

    if (profile === 'students') {
      const activeStudentIds = students.filter(s => s.Active).map(s => s.Rno.toString());
      activeStudentIds.forEach(studentId => {
        if (attendanceData[studentId]?.[today]) presentCount++;
      });
      total = activeStudentIds.length;
    } else {
      const activeStaffIds = students.filter(t => t.is_active).map(t => t.staff_no);
      activeStaffIds.forEach(teacherNo => {
        if (attendanceData[teacherNo]?.[today]) presentCount++;
      });
      total = activeStaffIds.length;
    }

    return { presentCount, totalStudents: total };
  };

  // Submit attendance
  const handleSubmitAttendance = async () => {
    if (profile === 'students' && !selectedClass) {
      showNotification('Please select a class first', 'error');
      return;
    }

    setIsSaving(true);
    
    try {
      if (profile === 'students') {
        // Get active students only
        const activeStudents = students.filter(student => student.Active);
        const activeStudentIds = activeStudents.map(student => student.Rno.toString());
        
        // Only process attendance for active students
        const filteredAttendanceData = {};
        activeStudentIds.forEach(id => {
          if (attendanceData[id]) {
            filteredAttendanceData[id] = attendanceData[id];
          }
        });
        
        const totalStudents = Object.keys(filteredAttendanceData).length;
        if (totalStudents === 0) {
          showNotification('No active students to update', 'info');
          setIsSaving(false);
          return;
        }
        
        // Initialize progress
        setUpdateProgress({ current: 0, total: totalStudents });
        
        // Submit all student attendance data in one bulk call
        await updateAttendanceBulk(selectedClass, selectedMonth, selectedYear, filteredAttendanceData);
        
        // Update progress for visual feedback
        setUpdateProgress({ current: totalStudents, total: totalStudents });
        
        // Refresh the attendance data
        const updatedAttendance = await fetchAttendance(selectedClass, selectedMonth, selectedYear);
        setAttendanceData(prevData => ({
          ...prevData,
          ...updatedAttendance
        }));
      } else if (profile === 'teachers') {
        // Get active teachers
        const activeTeachers = students.filter(teacher => teacher.is_active);
        const activeStaffIds = activeTeachers.map(teacher => teacher.staff_no);
        
        // Only process attendance for active teachers
        const filteredAttendanceData = {};
        activeStaffIds.forEach(id => {
          if (attendanceData[id]) {
            filteredAttendanceData[id] = attendanceData[id];
          }
        });
        
        const totalTeachers = Object.keys(filteredAttendanceData).length;
        if (totalTeachers === 0) {
          showNotification('No active teachers to update', 'info');
          setIsSaving(false);
          return;
        }
        
        // Initialize progress
        setUpdateProgress({ current: 0, total: totalTeachers });
        
        // Update all teacher attendance in one bulk call
        await updateStaffAttendanceBulk(selectedMonth, selectedYear, filteredAttendanceData);
        
        // Update progress for visual feedback
        setUpdateProgress({ current: totalTeachers, total: totalTeachers });
        
        // Refresh the attendance data
        const updatedAttendance = await fetchTeacherAttendance(selectedMonth, selectedYear);
        setAttendanceData(prevData => ({
          ...prevData,
          ...updatedAttendance
        }));
      }
      
      // Close the modal after a short delay to show the success state
      setTimeout(() => {
        setShowConfirmModal(false);
        // Reset progress after closing modal
        setTimeout(() => setUpdateProgress({ current: 0, total: 0 }), 500);
        showNotification(`${profile === 'students' ? 'Student' : 'Teacher'} attendance submitted successfully!`, 'success');
      }, 1000);
    } catch (error) {
      console.error('Error submitting attendance:', error);
      showNotification('Failed to submit attendance. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Calculate number of days a student was present in current month
  const getPresentDaysCount = (studentId) => {
    const studentAttendance = attendanceData[studentId];
    if (!studentAttendance) return 0;
    
    return Object.values(studentAttendance).filter(isPresent => isPresent).length;
  };

  // Render table headers (dates)
  const renderTableHeaders = () => {
    return (
      <tr>
        <th className="student-info">{profile === 'students' ? 'Student' : 'Teacher'}</th>
        {dayNumbers.map((day,id)=> (
          <th 
            key={`${id}+${day}`} 
            className={`date-cell ${isToday(day) ? 'today' : ''}`}
          >
            {day}
          </th>
        ))}
      </tr>
    );
  };
  
  // Render table rows (students or teachers)
  const renderTableRows = () => {
    return students.map(person => {
      // Handle different ID field names based on profile
      const id = profile === 'students' ? person.Rno : person.staff_no;
      const name = profile === 'students' ? person.Name : person.name;
      const presentDays = getPresentDaysCount(id);
      
      return (
        <tr key={id}>
          <td className="student-info">
            <div className="student-name">{name}</div>
            <div className="student-roll">
              {profile === 'students' ? 'Roll: ' : 'Code: '}{id} 
              <span className="present-days">Present: {presentDays} {presentDays === 1 ? 'day' : 'days'}</span>
            </div>
          </td>
          {dayNumbers.map(day => (
            <td 
              key={`${id}+${day}`} 
              className={`attendance-cell ${isToday(day) ? 'today' : ''}`}
            >
              <input
                type="checkbox"
                checked={attendanceData[id]?.[day] || false}
                onChange={(e) => handleAttendanceChange(id, day, e.target.checked)}
                disabled={!canEditAttendance(day)}
                className="attendance-checkbox"
              />
            </td>
          ))}
        </tr>
      );
    });
  };
  
  return (
    <div className="attendance-container">
      <header className="attendance-header">
        <h1>Attendance Management</h1>
        {((profile === 'students' && selectedClass) || profile === 'teachers') && (
          <div className="attendance-summary">
            {(() => {
              const { presentCount, totalStudents } = getAttendanceSummary();
              if (profile === 'students') {
                return `Students present today: ${presentCount}/${totalStudents}`;
              } else {
                return `Teachers present today: ${presentCount}/${totalStudents}`;
              }
            })()}
          </div>
        )}
      </header>
      {/* Filters */}
      <div className="filters-container">
        {user && user.role === 'admin' && (
          <div className="filter-group">
            <label htmlFor="profileSelect">Profile:</label>
            <select
              id="profileSelect"
              value={profile}
              onChange={handleProfileChange}
              className="filter-select"
            >
              <option value="students">Students</option>
              <option value="teachers">Teachers</option>
            </select>
          </div>
        )}

        {profile === 'students' && (
          <div className="filter-group">
            <label htmlFor="classSelect">Class:</label>
            <select
              id="classSelect"
              value={selectedClass}
              onChange={handleClassChange}
              className="filter-select"
            >
              <option value="">Select Class</option>
              {classes.map(cls => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>
        )}
        
        <div className="filter-group">
          <label htmlFor="monthSelect">Month:</label>
          <select
            id="monthSelect"
            value={selectedMonth}
            onChange={handleMonthChange}
            className="filter-select"
          >
            {months.map(month => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="yearSelect">Year:</label>
          <select
            id="yearSelect"
            value={selectedYear}
            onChange={handleYearChange}
            className="filter-select"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        
        <button 
          className="submit-attendance-btn"
          onClick={() => setShowConfirmModal(true)}
          disabled={(profile === 'students' && !selectedClass) || isLoading || isSaving}
        >
          {isSaving ? (
            <>
              <span className="spinner"></span>
              Saving...
            </>
          ) : (
            'Submit Today\'s Attendance'
          )}
        </button>
      </div>
      
      {/* Attendance Table */}
      <div className="attendance-table-container">
        {isLoading ? (
          <AttendanceSkeleton rows={5} days={daysInMonth} />
        ) : students.length === 0 ? (
          <div className="no-data-message">
            {selectedClass ? 
              'No active students found for this class.' : 
              'Please select a class to view attendance.'
            }
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="attendance-table">
              <thead>
                {renderTableHeaders()}
              </thead>
              <tbody>
                {renderTableRows()}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Attendance</h3>
              <button 
                className="close-modal-btn"
                onClick={() => !isSaving && setShowConfirmModal(false)}
                disabled={isSaving}
              >
                &times;
              </button>
            </div>
            
            <div className="attendance-modal-summary">
              {/* Show Profile Type */}
              <div className="summary-item">
                <span className="summary-label">Profile:</span>
                <span className="summary-value">{profile === 'students' ? 'Students' : 'Teachers'}</span>
              </div>

              {/* Show Class only for Students */}
              {profile === 'students' && (
                <div className="summary-item">
                  <span className="summary-label">Class:</span>
                  <span className="summary-value">{selectedClass}</span>
                </div>
              )}
              
              {/* Date is always shown */}
              <div className="summary-item">
                <span className="summary-label">Date:</span>
                <span className="summary-value">
                  {new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    weekday: 'long'
                  })}
                </span>
              </div>
              
              {/* Attendance counts */}
              <div className="summary-item">
                <span className="summary-label">Total {profile === 'students' ? 'Students' : 'Teachers'}:</span>
                <span className="summary-value">{getAttendanceSummary().totalStudents}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Present:</span>
                <span className="summary-value present">
                  {getAttendanceSummary().presentCount}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Absent:</span>
                <span className="summary-value absent">
                  {getAttendanceSummary().totalStudents - getAttendanceSummary().presentCount}
                </span>
              </div>
            </div>
            
            <p className="confirmation-text">Are you sure you want to submit today's attendance?</p>
            
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowConfirmModal(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary submit-attendance-modal-btn" 
                onClick={handleSubmitAttendance}
                disabled={isSaving}
                style={updateProgress.current > 0 ? { backgroundColor: '#28a745', color: 'black', fontWeight: 600 } : {}}
              >
                {isSaving ? 
                  (updateProgress.current > 0 ? 
                    `Saving... (${updateProgress.current}/${updateProgress.total})` : 
                    'Saving...') : 
                  (updateProgress.current > 0 ? 
                    `✓ Updated ${updateProgress.current} ${profile === 'students' ? 'student' : 'teacher'}${updateProgress.current > 1 ? 's' : ''}` : 
                    'Submit Attendance')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Notification */}
      {notification.show && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}
    </div>
  );
}

export default Attendance;
